// Shared, dependency-free invoice-number sequencing logic. Used by the
// frontend (to suggest the next number while a Create draft is open) and the
// backend (to assign a real number server-side for flows with no client-side
// form step, e.g. duplicating an invoice). Keeping this in `common/` is the
// single source of truth for the increment/pad/prefix rules — do not
// reimplement this logic in either layer.
interface ParsedNumber {
    prefix: string;
    digits: string;
}

function parseTrailingNumber(value: string): ParsedNumber | null {
    const match = /^(.*?)(\d+)$/.exec(value);
    if (!match) {
        return null;
    }

    const [, prefix, digits] = match;
    return { prefix, digits };
}

function stripLeadingZeros(digits: string): string {
    const stripped = digits.replace(/^0+/, "");
    return stripped === "" ? "0" : stripped;
}

function compareDigits(a: string, b: string): number {
    const aStripped = stripLeadingZeros(a);
    const bStripped = stripLeadingZeros(b);
    if (aStripped.length !== bStripped.length) {
        return aStripped.length > bStripped.length ? 1 : -1;
    }
    if (aStripped === bStripped) {
        return 0;
    }
    return aStripped > bStripped ? 1 : -1;
}

function incrementDigits(digits: string): string {
    const { result, carry } = digits.split("").reduceRight<{
        result: string;
        carry: number;
    }>(
        (acc, digit) => {
            const sum = Number(digit) + acc.carry;
            return {
                result: `${sum % 10}${acc.result}`,
                carry: sum >= 10 ? 1 : 0,
            };
        },
        { result: "", carry: 1 },
    );
    return carry > 0 ? `1${result}` : result;
}

export function suggestNextInvoiceNumber(
    last: string | null | undefined,
    fallback: string,
): string {
    if (!last) {
        return fallback;
    }

    const parsed = parseTrailingNumber(last);
    if (!parsed) {
        return fallback;
    }

    const incremented = incrementDigits(parsed.digits);
    const padded = incremented.padStart(parsed.digits.length, "0");
    return `${parsed.prefix}${padded}`;
}

const DEFAULT_ENTITY_PREFIX = "INV";
const ENTITY_PREFIX_LENGTH = 3;
const INVOICE_SEQUENCE_LENGTH = 4;

/**
 * Derives a 3-letter uppercase invoice prefix from a business entity name.
 *
 * Collects the initial letter of each word, then (when there are fewer than
 * three words) tops up using the remaining letters of the last word until three
 * letters are reached. Non-alphabetic characters are stripped before deriving
 * letters. Falls back to "INV" when no usable letters remain.
 *
 * Examples: "Internet Money" -> "IMO", "Acme" -> "ACM", "3M Company" -> "MCO".
 */
export function entityInvoicePrefix(name: string | null | undefined): string {
    if (!name) {
        return DEFAULT_ENTITY_PREFIX;
    }

    const words = name
        .split(/\s+/)
        .map((word) => word.replace(/[^a-zA-Z]/g, ""))
        .filter((word) => word.length > 0);

    if (words.length === 0) {
        return DEFAULT_ENTITY_PREFIX;
    }

    let letters = words
        .map((word) => word[0])
        .slice(0, ENTITY_PREFIX_LENGTH)
        .join("");

    if (letters.length < ENTITY_PREFIX_LENGTH) {
        const lastWord = words[words.length - 1];
        const remainder = lastWord.slice(1);
        for (const char of remainder) {
            if (letters.length >= ENTITY_PREFIX_LENGTH) {
                break;
            }
            letters += char;
        }
    }

    const prefix = letters.toUpperCase();
    return prefix.length === ENTITY_PREFIX_LENGTH
        ? prefix
        : DEFAULT_ENTITY_PREFIX;
}

/**
 * Suggests the next invoice number for a given entity. When the entity already
 * has a number, continues its sequence with the shared, width-preserving
 * increment (e.g. "INV-100" -> "INV-101", "INV-0042" -> "INV-0043"). Only the
 * very first invoice is seeded from the entity-derived prefix padded to 4
 * digits (e.g. "Internet Money" -> "IMO-0001").
 */
export function suggestNextInvoiceNumberForEntity({
    last,
    entityName,
}: {
    last: string | null | undefined;
    entityName: string | null | undefined;
}): string {
    const prefix = entityInvoicePrefix(entityName);
    const fallback = `${prefix}-${"1".padStart(INVOICE_SEQUENCE_LENGTH, "0")}`;

    // `last` is already scoped to this entity by the caller, so always continue
    // its sequence — including legacy/custom prefixes (e.g. "INV-0042" ->
    // "INV-0043"). Resetting to `${prefix}-0001` on a prefix mismatch would
    // suggest a lower/duplicate number for entities that predate the
    // entity-derived prefix scheme. The derived prefix only seeds the very
    // first invoice.
    //
    // Preserve the source's own sequence width rather than re-padding to 4
    // digits: a source of "INV-100" must continue as "INV-101", not "INV-0101"
    // (re-padding would widen the number and could collide with an existing
    // one). `suggestNextInvoiceNumber` already returns the fallback for a
    // missing/unparseable `last`.
    return suggestNextInvoiceNumber(last, fallback);
}

export function pickLatestInvoiceNumber(
    candidates: ReadonlyArray<string | null | undefined>,
): string | null {
    let anchorPrefix: string | null = null;
    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }
        const parsed = parseTrailingNumber(candidate);
        if (parsed) {
            anchorPrefix = parsed.prefix;
        }
    }

    if (anchorPrefix === null) {
        return null;
    }

    let best: { parsed: ParsedNumber; source: string } | null = null;
    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }
        const parsed = parseTrailingNumber(candidate);
        if (!parsed || parsed.prefix !== anchorPrefix) {
            continue;
        }

        if (!best) {
            best = { parsed, source: candidate };
            continue;
        }

        const cmp = compareDigits(parsed.digits, best.parsed.digits);
        if (
            cmp > 0 ||
            (cmp === 0 && parsed.digits.length > best.parsed.digits.length)
        ) {
            best = { parsed, source: candidate };
        }
    }

    return best?.source ?? null;
}
